import { usEventIdentity, type UsGameIdentity } from '../../relay/src/polymarketUs/gameIdentity';
export type { UsGameIdentity } from '../../relay/src/polymarketUs/gameIdentity';
import { SourceContinuityTransport, type SourceContinuity, type SourceLane } from '../lib/gameState/sourceContinuity';
import { elapsedSince, localClockStamp, type LocalClockStamp } from '../lib/relayClock';
import { US_GAME_SPORTS, normalizeUsGameKey, usPeriodForMarketType, usGameSportForBoard, type UsGameSport } from '../lib/polymarketUs/sports';
import type { UsDiscoveryMarket } from '../lib/polymarketUs/marketClient';
import { usFullGameTotalFamily } from '../lib/polymarketUs/totalTarget';
import { readUsMarketStatistics, type UsMarketStatistics } from '../lib/polymarketUs/statistics';

/** Display-only quote. It is never account, rule, catalogue or execution evidence. */
export interface UsBrowserBoardQuote {
  marketSlug: string; gameKey: string; yesTeam: string; sport: UsGameSport;
  book: Pick<UsDiscoveryMarket['book'], 'observedAtMs' | 'yesBidUsd' | 'yesAskUsd' | 'noBidUsd' | 'noAskUsd'>;
  statistics?: UsMarketStatistics | null;
  currentAt(): boolean;
}
export type UsBoardQuote = UsDiscoveryMarket | UsBrowserBoardQuote;
/** #1231 — display-only US full-game spread or total quote, with the catalogue
 *  fields the ticket's own joins read (`pickUsSpread`, `pickUsFullGameTotal`).
 *  `line` is the producer strike as exact text: the YES team's signed spread
 *  ("-3.5") or the total's threshold ("44.5"). Never account, rule, catalogue
 *  or execution evidence. */
export interface UsBrowserLineQuote {
  marketSlug: string; marketId: string; gameKey: string; sport: UsGameSport;
  marketType: 'spreads' | 'totals'; sportsMarketType: string; period: 'full';
  yesTeam: string | null; line: string;
  book: UsBrowserBoardQuote['book'];
  statistics?: UsMarketStatistics | null;
  currentAt(): boolean;
}
type QuoteCore = { marketSlug: string; gameKey: string; sport: UsGameSport; book: UsBrowserBoardQuote['book']; statistics?: UsMarketStatistics | null; currentAt(): boolean };
/** What an admission rule returns for an admitted row: the quote minus the shared core. */
type Identity<Q> = Omit<Q, 'marketSlug' | 'gameKey' | 'sport' | 'book' | 'statistics' | 'currentAt'>;
const object = (v: unknown): Record<string, unknown> | null => v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null;
const positive = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;
const price = (v: unknown): string | null => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100 ? String(v / 100) : null;
// Matches the existing worker's 45s transport watchdog; pings run every 30s.
export const US_BROWSER_TRANSPORT_LEASE_MS = 45_000;
interface Feed { connected: boolean; session: object; control: SourceContinuityTransport; alive: LocalClockStamp | null; ageMs: number }
interface Entry<Q> { feed: string; session: object; ticker: string; transportSport: string; raw: Record<string, unknown>; quote: Q; withdrawn: boolean }

/** Isolated public US board adapter behind marketStore's existing publish cadence.
 * The owner predicate is the fleet's existing per-pair admission decision.
 * No sockets, polling, timers, account data or generic-venue row conversion.
 * The feed authority (lease, lane, generation) is shared; each subclass names
 * which rows it admits. */
abstract class UsQuoteRows<Q extends QuoteCore> {
  private feeds = new Map<string, Feed>();
  protected rows = new Map<string, Entry<Q>>();
  constructor(protected readonly owns: (feed: string, sport: string, ticker: string) => boolean) {}
  /** The kind-specific half of admission; null refuses the row. */
  protected abstract admit(r: Record<string, unknown>, key: string, sport: UsGameSport): Identity<Q> | null;
  status(id: string, connected: boolean): void {
    const prior = this.feeds.get(id);
    if (connected && prior?.connected) return;
    this.feeds.set(id, { connected, session: {}, control: new SourceContinuityTransport(), alive: null, ageMs: 0 });
    for (const e of this.rows.values()) if (e.feed === id) e.withdrawn = true;
  }
  alive(id: string, ageMs = 0): void {
    const f = this.feeds.get(id);
    if (!f?.connected || !Number.isFinite(ageMs) || ageMs < 0) return;
    // A late heartbeat cannot resurrect rows whose lease already expired.
    if (f.alive && elapsedSince(f.alive) + f.ageMs >= US_BROWSER_TRANSPORT_LEASE_MS) {
      for (const e of this.rows.values()) if (e.feed === id) e.withdrawn = true;
    }
    f.alive = localClockStamp(); f.ageMs = ageMs;
  }
  control(id: string, frame: SourceContinuity | null, invalidLane?: SourceLane): void {
    const f = this.feeds.get(id);
    if (!f?.connected) return;
    if (frame && !frame.lane.startsWith('polyus-book:') || !frame && invalidLane && !invalidLane.startsWith('polyus-book:')) return;
    if (!frame) {
      if (invalidLane) f.control.quarantine(invalidLane); else f.control.quarantine(undefined);
      for (const e of this.rows.values()) if (e.feed === id && (!invalidLane || e.raw.lane === invalidLane)) e.withdrawn = true;
      return;
    }
    const prior = f.control.get(frame.lane);
    const result = f.control.receive(frame);
    if (result === 'ignored') return;
    if (result !== 'accepted' || frame.state !== 'valid' || prior && prior.sourceGen !== frame.sourceGen) {
      for (const e of this.rows.values()) if (e.feed === id && e.raw.lane === frame.lane) e.withdrawn = true;
    }
  }
  protected qualifies(e: Entry<Q>): boolean {
    const f = this.feeds.get(e.feed), r = e.raw;
    if (e.withdrawn || !f?.connected || f.session !== e.session || !f.alive
      || elapsedSince(f.alive) + f.ageMs >= US_BROWSER_TRANSPORT_LEASE_MS
      || f.control.invalidated || f.control.quarantinedLanes.includes(r.lane as SourceLane)
      || !positive(r.observedAtMs) || r.pendingResync !== undefined && r.pendingResync !== false
      || r.lane !== `polyus-book:${e.transportSport}` || !this.owns(e.feed, e.transportSport, e.ticker)) return false;
    const governing = f.control.get(r.lane as SourceLane);
    return governing?.state === 'valid' && governing.sourceEpoch === r.sourceEpoch && governing.sourceGen === r.sourceGen;
  }
  ingest(value: unknown, id: string): UsGameSport | null {
    const r = object(value), ticker = r?.ticker;
    if (!r || typeof ticker !== 'string' || !ticker.startsWith('PMUS-')) return null;
    const old = this.rows.get(ticker), slug = r.pmusMarketSlug;
    const transportSport = typeof r.feedSport === 'string' ? r.feedSport.toUpperCase() : typeof r.sport === 'string' ? r.sport.toUpperCase() : '';
    const base = typeof r.baseSport === 'string' ? r.baseSport : r.sport;
    const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
    const key = sport ? normalizeUsGameKey(r.canonicalGameKeyV2, sport) : null;
    const f = this.feeds.get(id);
    const identity = r.source !== 'polymarket_us' || r.venue !== 'polymarket_us' || !sport || !key || !key.startsWith(sport.toLowerCase()+'|')
      || typeof slug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(slug) || ticker !== `PMUS-${slug}`
      || r.marketType !== 'game'
      || transportSport !== sport && transportSport !== `${sport}ALT`
      || r.status !== 'open' || !f?.connected ? null : this.admit(r, key, sport);
    if (!identity || !f || !sport || !key || typeof slug !== 'string') {
      if (old?.feed === id) old.withdrawn = true;
      return old?.quote.sport ?? null;
    }
    if (!old && this.rows.size >= 4096) return null;
    let statistics = readUsMarketStatistics(r.pmusStatistics, slug);
    if (statistics && old?.feed === id && old.session === f.session && old.raw.sourceEpoch === r.sourceEpoch
      && old.raw.sourceGen === r.sourceGen && old.raw.lane === r.lane && old.quote.statistics
      && statistics.observedAtMs < old.quote.statistics.observedAtMs) statistics = old.quote.statistics;
    const quote = { marketSlug: slug, gameKey: key, sport, ...identity, statistics,
      book: { observedAtMs: positive(r.observedAtMs) ? r.observedAtMs : null,
        yesBidUsd: price(r.yesBid), yesAskUsd: price(r.yesAsk), noBidUsd: price(r.noBid), noAskUsd: price(r.noAsk) },
      currentAt: () => this.rows.get(ticker) === entry && this.qualifies(entry) } as unknown as Q;
    const entry: Entry<Q> = { ticker, feed: id, session: f.session, transportSport, raw: { ...r, pmusStatistics: statistics }, quote, withdrawn: false };
    // A delayed observation cannot roll back a quote within the same source.
    if (old?.feed === id && old.session === f.session && old.raw.sourceEpoch === r.sourceEpoch
      && old.raw.sourceGen === r.sourceGen && positive(old.raw.observedAtMs) && positive(r.observedAtMs)
      && r.observedAtMs < old.raw.observedAtMs) return null;
    // Retired-generation frames cannot replace a currently qualified quote.
    if (old && this.qualifies(old) && !this.qualifies(entry)) {
      if (old.feed === id && (r.sourceEpoch === undefined || r.sourceEpoch === old.raw.sourceEpoch)
        && (r.sourceGen === undefined || r.sourceGen === old.raw.sourceGen)) {
        old.withdrawn = true;
        return sport;
      }
      return null;
    }
    this.rows.set(ticker, entry); return sport;
  }
  book(id: string, ticker: string, value: unknown, observation: unknown): UsGameSport | null {
    const old = this.rows.get(ticker), book = object(value), evidence = object(observation);
    if (!old || old.feed !== id || !book || !evidence || book.ticker != null && book.ticker !== ticker) return null;
    const best = (levels: unknown): number | null | undefined => {
      if (!Array.isArray(levels)) return undefined;
      let top: number | null = null;
      for (const level of levels) {
        const l = object(level);
        if (!l || typeof l.price !== 'number' || !Number.isFinite(l.price) || l.price < 0 || l.price > 100
          || typeof l.size !== 'number' || !Number.isFinite(l.size) || l.size < 0) return undefined;
        if (l.size > 0 && (top === null || l.price > top)) top = l.price;
      }
      return top;
    };
    const yes = best(book.yes), no = best(book.no);
    if (yes === undefined || no === undefined) return this.withdraw(id, ticker);
    const complement = (n: number) => Math.round((100 - n) * 1e8) / 1e8;
    const authorityChanged = ['sourceEpoch', 'sourceGen', 'lane'].some(key => evidence[key] !== undefined && evidence[key] !== old.raw[key]);
    const statistics = Object.prototype.hasOwnProperty.call(evidence, 'pmusStatistics') ? evidence.pmusStatistics
      : authorityChanged || old.withdrawn || old.session !== this.feeds.get(id)?.session ? null : old.raw.pmusStatistics;
    return this.ingest({ ...old.raw, ...evidence, pmusStatistics: statistics, yesBid: yes, noBid: no,
      yesAsk: no === null ? null : complement(no), noAsk: yes === null ? null : complement(yes) }, id);
  }
  withdraw(id: string, ticker: string): UsGameSport | null {
    const e = this.rows.get(ticker);
    if (!e || e.feed !== id) return null;
    e.withdrawn = true; return e.quote.sport;
  }
  remove(id: string, ticker: string): void { if (this.rows.get(ticker)?.feed === id) this.rows.delete(ticker); }
  reset(id: string, passes: (raw: Record<string, unknown>) => boolean): void {
    for (const [ticker, e] of this.rows) if (e.feed === id && !passes(e.raw)) this.rows.delete(ticker);
  }
  retireUnowned(): void {
    for (const e of this.rows.values()) if (!this.owns(e.feed, e.transportSport, e.ticker)) e.withdrawn = true;
  }
  retainFeeds(ids: readonly string[]): void {
    const live = new Set(ids);
    for (const id of this.feeds.keys()) if (!live.has(id)) this.feeds.delete(id);
    for (const [ticker, e] of this.rows) if (!live.has(e.feed)) this.rows.delete(ticker);
  }
  /** #1231 identity lookup: the US game key of an owned row for this slug,
   *  whatever its freshness (identity, not price evidence). */
  gameKeyForSlug(slug: string): string | null {
    const e = this.rows.get(`PMUS-${slug}`);
    return e && this.owns(e.feed, e.transportSport, e.ticker) ? e.quote.gameKey : null;
  }
  /** Exact held-market display quote. Keep a withdrawn row so the caller can
   * withhold its mark instead of falling back to an older catalogue price. */
  quoteForSlug(slug: string): Q | null {
    const e = this.rows.get(`PMUS-${slug}`);
    return e?.quote ?? null;
  }
}

/** #1232: game navigation identity only. No REST price or account authority.
 * US publishes an oriented object; Intl's alphabetical pair is not equivalent. */
export function usGameIdentity(value: unknown): UsGameIdentity | null {
  const r = object(value), game = usEventIdentity(value);
  return game && r?.pmusMarketType === 'moneyline' && typeof r.canonicalYesTeam === 'string'
    && [game.awayCode, game.homeCode].includes(r.canonicalYesTeam) ? game : null;
}
/** #1262: a native line's typed identity, with no fabricated venue book. */
export interface UsLineIdentity {
  game: UsGameIdentity; marketSlug: string; marketType: 'spreads' | 'totals';
  line: number; favored: 'away' | 'home' | null;
}
export function usLineIdentity(value: unknown): UsLineIdentity | null {
  const r = object(value), game = usEventIdentity(value);
  if (!r || !game || r.period !== 'full' || strikeText(r.floorStrike) === null
    || typeof r.pmusMarketId !== 'string' || !/^[0-9]{1,32}$/.test(r.pmusMarketId)) return null;
  const strike = Number(r.floorStrike), type = r.sportsMarketType as string, marketSlug = r.pmusMarketSlug as string;
  if (r.pmusMarketType === 'spreads' && /full_game_spread$/.test(type) && strike !== 0
    && typeof r.canonicalYesTeam === 'string' && [game.awayCode, game.homeCode].includes(r.canonicalYesTeam)) {
    const favored = (strike < 0 ? r.canonicalYesTeam === game.awayCode : r.canonicalYesTeam !== game.awayCode) ? 'away' : 'home';
    return { game, marketSlug, marketType: 'spreads', favored, line: (favored === 'away' ? -1 : 1) * Math.abs(strike) };
  }
  if (r.pmusMarketType === 'totals' && usFullGameTotalFamily(type, game.sport) && strike > 0 && r.canonicalYesTeam == null)
    return { game, marketSlug, marketType: 'totals', favored: null, line: strike };
  return null;
}
const NO_US_LINES: UsLineIdentity[] = [];
class UsLineIdentityIndex {
  private bySport = new Map<UsGameSport, UsLineIdentity[]>();
  replace(values: UsLineIdentity[]): void {
    this.bySport.clear();
    // A conflicting event orientation cannot supply standalone rows.
    const valid = new Set(uniqueUsGames(values.map(line => line.game), US_GAME_SPORTS).map(game => game.key));
    const unique = new Map<string, UsLineIdentity>(), conflicts = new Set<string>();
    for (const line of values) {
      const old = unique.get(line.marketSlug);
      if (old && (old.game.key !== line.game.key || old.line !== line.line || old.favored !== line.favored || old.marketType !== line.marketType)) conflicts.add(line.marketSlug);
      else unique.set(line.marketSlug, line);
    }
    for (const line of unique.values()) {
      if (!valid.has(line.game.key) || conflicts.has(line.marketSlug)) continue;
      const lines = this.bySport.get(line.game.sport) ?? [];
      lines.push(line); this.bySport.set(line.game.sport, lines);
    }
  }
  lines(sport: UsGameSport): UsLineIdentity[] { return this.bySport.get(sport) ?? NO_US_LINES; }
}

/** Conflicting oriented identity is not a safe navigation target. */
function uniqueUsGames(values: Iterable<UsGameIdentity>, sports: readonly UsGameSport[]): UsGameIdentity[] {
  const games = new Map<string, UsGameIdentity>(), conflicts = new Set<string>();
  for (const game of values) {
    if (!sports.includes(game.sport)) continue;
    const old = games.get(game.key);
    if (old && (old.awayCode !== game.awayCode || old.homeCode !== game.homeCode || old.startTimeMs !== game.startTimeMs)) conflicts.add(game.key);
    else games.set(game.key, game);
  }
  for (const key of conflicts) games.delete(key);
  return [...games.values()];
}

const NO_US_GAMES: UsGameIdentity[] = [];
const NO_US_KEYS: string[] = [];
/** Rebuilt at metadata boundaries, not quote/display cadence. */
class UsGameIdentityIndex {
  private bySport = new Map<UsGameSport, UsGameIdentity[]>();
  private wires = new Map<string, string[]>();
  replace(values: UsGameIdentity[]): void {
    this.bySport.clear(); this.wires.clear();
    const wireSets = new Map<string, Set<string>>();
    for (const game of values) {
      const wires = wireSets.get(game.key) ?? new Set<string>();
      wires.add(game.wireKey); wireSets.set(game.key, wires);
    }
    const unique = uniqueUsGames(values, US_GAME_SPORTS);
    for (const game of unique) {
      const games = this.bySport.get(game.sport) ?? [];
      games.push(game); this.bySport.set(game.sport, games);
      this.wires.set(game.key, [...wireSets.get(game.key)!].sort());
    }
  }
  games(sports: readonly UsGameSport[]): UsGameIdentity[] {
    return sports.length === 1 ? this.bySport.get(sports[0]) ?? NO_US_GAMES : sports.flatMap(sport => this.bySport.get(sport) ?? NO_US_GAMES);
  }
  wireKeys(gameKey: string): string[] { return this.wires.get(gameKey) ?? NO_US_KEYS; }
}
const identityFields = ['source', 'venue', 'marketType', 'pmusMarketType', 'status', 'dhAmbiguous',
  'sportsMarketType', 'period', 'pmusMarketSlug', 'ticker', 'baseSport', 'sport', 'canonicalGameKeyV2',
  'canonicalYesTeam', 'eventStartTime', 'floorStrike', 'pmusMarketId'] as const;
function sameGameIdentity(a: Record<string, unknown> | undefined, b: Record<string, unknown> | undefined): boolean {
  if (!a || !b) return a === b;
  if (identityFields.some(field => a[field] !== b[field])) return false;
  const at = object(a.canonicalTeams), bt = object(b.canonicalTeams);
  return at?.away === bt?.away && at?.home === bt?.home;
}

/** The full-game moneyline quotes the board and Game Detail show. */
export class UsBoardQuotes extends UsQuoteRows<UsBrowserBoardQuote> {
  private identityIndex = new UsGameIdentityIndex();
  private identityDirty = true;
  override ingest(value: unknown, id: string): UsGameSport | null {
    const ticker = object(value)?.ticker;
    const before = typeof ticker === 'string' ? this.rows.get(ticker) : undefined;
    const result = super.ingest(value, id);
    const after = typeof ticker === 'string' ? this.rows.get(ticker) : undefined;
    if (before?.feed !== after?.feed || !sameGameIdentity(before?.raw, after?.raw)) this.identityDirty = true;
    return result;
  }
  override remove(id: string, ticker: string): void { super.remove(id, ticker); this.identityDirty = true; }
  override reset(id: string, passes: (raw: Record<string, unknown>) => boolean): void { super.reset(id, passes); this.identityDirty = true; }
  override retireUnowned(): void { super.retireUnowned(); this.identityDirty = true; }
  override retainFeeds(ids: readonly string[]): void { super.retainFeeds(ids); this.identityDirty = true; }
  private currentIdentityIndex(): UsGameIdentityIndex {
    if (this.identityDirty) {
      const games: UsGameIdentity[] = [];
      for (const e of this.rows.values()) {
        if (!this.owns(e.feed, e.transportSport, e.ticker)) continue;
        const identity = usGameIdentity(e.raw);
        if (identity) games.push(identity);
      }
      this.identityIndex.replace(games); this.identityDirty = false;
    }
    return this.identityIndex;
  }
  /** Broad-feed / Game Detail fallback when no board index is mounted. */
  games(sports: readonly UsGameSport[]): UsGameIdentity[] { return this.currentIdentityIndex().games(sports); }
  wireKeys(gameKey: string): string[] { return this.currentIdentityIndex().wireKeys(gameKey); }
  protected admit(r: Record<string, unknown>, key: string): Identity<UsBrowserBoardQuote> | null {
    const team = r.canonicalYesTeam;
    if (r.pmusMarketType !== 'moneyline'
      || typeof r.sportsMarketType !== 'string' || usPeriodForMarketType(r.sportsMarketType) !== 'full'
      || r.period !== undefined && r.period !== 'full'
      || typeof team !== 'string' || !key.split('|').slice(3).includes(team)) return null;
    return { yesTeam: team };
  }
  quotes(sports: readonly UsGameSport[]): ReadonlyMap<string, UsBrowserBoardQuote> {
    return this.owned(e => sports.includes(e.quote.sport));
  }
  /** One game's quote (US canonical game key) under the same owner and
   *  ambiguity rule as `quotes()`. Still one pass over every row, not an
   *  index lookup; it only skips building a per-sport map. */
  quoteForGame(gameKey: string): UsBrowserBoardQuote | null {
    return this.owned(e => e.quote.gameKey === gameKey).get(gameKey) ?? null;
  }
  // Owned rows by game key; a key two owned rows claim is dropped, never guessed.
  private owned(match: (e: Entry<UsBrowserBoardQuote>) => boolean): Map<string, UsBrowserBoardQuote> {
    const result = new Map<string, UsBrowserBoardQuote>(), ambiguous = new Set<string>();
    for (const e of this.rows.values()) {
      if (!match(e) || !this.owns(e.feed, e.transportSport, e.ticker)) continue;
      const key = e.quote.gameKey;
      if (result.has(key)) ambiguous.add(key); else result.set(key, e.quote);
    }
    for (const key of ambiguous) result.delete(key);
    return result;
  }
}

/** Exact strike text for a finite producer number with at most two decimals
 *  (the relay's grammar, coreFeed.ts), or null. */
const strikeText = (v: unknown): string | null => {
  if (typeof v !== 'number' || !Number.isFinite(v) || Math.abs(v) > 1000) return null;
  const t = String(v);
  return /^-?[0-9]{1,4}(\.[0-9]{1,2})?$/.test(t) ? t : null;
};

/** #1231 — US full-game spreads and supported native totals, under the same
 *  feed authority as the moneyline quote. Period lines, team totals, props and
 *  families G1 does not support are refused, never guessed. */
export class UsLineQuotes extends UsQuoteRows<UsBrowserLineQuote> {
  private identityIndex = new UsLineIdentityIndex();
  private gameIndex = new UsGameIdentityIndex();
  private identityDirty = true;
  override ingest(value: unknown, id: string): UsGameSport | null {
    const ticker = object(value)?.ticker, before = typeof ticker === 'string' ? this.rows.get(ticker) : undefined;
    const result = super.ingest(value, id), after = typeof ticker === 'string' ? this.rows.get(ticker) : undefined;
    if (before?.feed !== after?.feed || !sameGameIdentity(before?.raw, after?.raw)) this.identityDirty = true;
    return result;
  }
  override remove(id: string, ticker: string): void { super.remove(id, ticker); this.identityDirty = true; }
  override reset(id: string, passes: (raw: Record<string, unknown>) => boolean): void { super.reset(id, passes); this.identityDirty = true; }
  override retireUnowned(): void { super.retireUnowned(); this.identityDirty = true; }
  override retainFeeds(ids: readonly string[]): void { super.retainFeeds(ids); this.identityDirty = true; }
  private refreshIdentity(): void {
    if (this.identityDirty) {
      const lines: UsLineIdentity[] = [];
      for (const e of this.rows.values()) {
        if (!this.owns(e.feed, e.transportSport, e.ticker)) continue;
        const line = usLineIdentity(e.raw); if (line) lines.push(line);
      }
      this.identityIndex.replace(lines); this.gameIndex.replace(lines.map(line => line.game)); this.identityDirty = false;
    }
  }
  inventory(sport: UsGameSport): UsLineIdentity[] { this.refreshIdentity(); return this.identityIndex.lines(sport); }
  games(sports: readonly UsGameSport[]): UsGameIdentity[] { this.refreshIdentity(); return this.gameIndex.games(sports); }
  wireKeys(key: string): string[] { this.refreshIdentity(); return this.gameIndex.wireKeys(key); }
  protected admit(r: Record<string, unknown>, key: string, sport: UsGameSport): Identity<UsBrowserLineQuote> | null {
    const marketId = r.pmusMarketId, type = r.sportsMarketType, line = strikeText(r.floorStrike);
    if (typeof marketId !== 'string' || !/^[0-9]{1,32}$/.test(marketId) || typeof type !== 'string'
      || r.period !== 'full' || line === null) return null;
    if (r.pmusMarketType === 'spreads') {
      const team = r.canonicalYesTeam;
      if (!/full_game_spread$/.test(type) || typeof team !== 'string' || !key.split('|').slice(3).includes(team)) return null;
      return { marketId, marketType: 'spreads', sportsMarketType: type, period: 'full', yesTeam: team, line };
    }
    if (r.pmusMarketType === 'totals') {
      if (!usFullGameTotalFamily(type, sport) || r.canonicalYesTeam != null || !(Number(line) > 0)) return null;
      return { marketId, marketType: 'totals', sportsMarketType: type, period: 'full', yesTeam: null, line };
    }
    return null;
  }
  /** Owned line quotes for one US game, current or not (a non-current one is
   *  shown disabled with its reason, not as absent). Ambiguity is the picks'
   *  call, never this. */
  lineCandidates(gameKey: string): { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] } {
    const spreads: UsBrowserLineQuote[] = [], totals: UsBrowserLineQuote[] = [];
    for (const e of this.rows.values()) {
      if (e.quote.gameKey !== gameKey || !this.owns(e.feed, e.transportSport, e.ticker)) continue;
      (e.quote.marketType === 'spreads' ? spreads : totals).push(e.quote);
    }
    return { spreads, totals };
  }
  /** US game keys holding at least one owned line row, including native
   *  US-only games admitted by the board's exact identity index. */
  gamesWithLines(sports: readonly UsGameSport[]): ReadonlySet<string> {
    const games = new Set<string>();
    for (const e of this.rows.values()) {
      if (sports.includes(e.quote.sport) && this.owns(e.feed, e.transportSport, e.ticker)) games.add(e.quote.gameKey);
    }
    return games;
  }
}

/** #1231 — slug → US game key for every full-game moneyline, spread and total
 *  the board's US index read names (identity only: no prices). Each source's
 *  successful read replaces its entries; a failed read leaves them. */
export class UsSlugIdentity {
  private sources = new Map<string, Map<string, string>>();
  private gameSources = new Map<string, UsGameIdentity[]>();
  private lineSources = new Map<string, UsLineIdentity[]>();
  private lineIndex = new UsLineIdentityIndex();
  private identityIndex = new UsGameIdentityIndex();
  set(sourceId: string, rows: readonly unknown[]): void {
    const slugs = new Map<string, string>();
    for (const row of rows) {
      const r = object(row), slug = r?.pmusMarketSlug, type = r?.pmusMarketType;
      if (!r || r.source !== 'polymarket_us' || r.venue !== 'polymarket_us' || r.marketType !== 'game'
        || r.status !== 'open' || typeof slug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(slug)
        || r.ticker !== `PMUS-${slug}` || typeof r.sportsMarketType !== 'string') continue;
      const base = typeof r.baseSport === 'string' ? r.baseSport : r.sport;
      const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
      const key = sport ? normalizeUsGameKey(r.canonicalGameKeyV2, sport) : null;
      if (!sport || !key || !key.startsWith(sport.toLowerCase() + '|')) continue;
      const full = type === 'moneyline'
        ? usPeriodForMarketType(r.sportsMarketType) === 'full' && (r.period === undefined || r.period === 'full')
        : (type === 'spreads' && /full_game_spread$/.test(r.sportsMarketType)
          || type === 'totals' && usFullGameTotalFamily(r.sportsMarketType, sport) !== null) && r.period === 'full';
      if (full) slugs.set(slug, key);
    }
    this.sources.set(sourceId, slugs);
    const lines = rows.map(usLineIdentity).filter((line): line is UsLineIdentity => line !== null);
    this.lineSources.set(sourceId, lines); this.lineIndex.replace([...this.lineSources.values()].flat());
    this.gameSources.set(sourceId, [...rows.map(usGameIdentity).filter((g): g is UsGameIdentity => g !== null), ...lines.map(line => line.game)]);
    this.identityIndex.replace([...this.gameSources.values()].flat());
  }
  clear(sourceId: string): void {
    this.sources.delete(sourceId);
    if (this.lineSources.delete(sourceId)) this.lineIndex.replace([...this.lineSources.values()].flat());
    if (this.gameSources.delete(sourceId)) this.identityIndex.replace([...this.gameSources.values()].flat());
  }
  games(sports: readonly UsGameSport[]): UsGameIdentity[] {
    return this.identityIndex.games(sports);
  }
  lines(sport: UsGameSport): UsLineIdentity[] { return this.lineIndex.lines(sport); }
  wireKeys(gameKey: string): string[] { return this.identityIndex.wireKeys(gameKey); }
  /** Whether any source has published a read (then it is the authority). */
  published(): boolean { return this.sources.size > 0; }
  get(slug: string): string | null {
    for (const slugs of this.sources.values()) { const key = slugs.get(slug); if (key) return key; }
    return null;
  }
}

/** #1231 / #1232 — the US canonical game key for a full-game US moneyline,
 *  spread or total slug. Once the board's identity index has been published it
 *  is the authority (a slug the latest successful read dropped answers null);
 *  before the first read, the two quote stores answer. Identity only: a stale,
 *  withdrawn or since-CLOSED market still answers until a source drops it, so a
 *  non-null answer does not mean the market is open. Null for an unknown slug,
 *  a prop, team-total or period market, or a game not on the slate.
 *  Spread/total identity is published only while the board is on the
 *  Spread/Total reading; on the Moneyline reading only moneyline slugs answer. */
export function gameKeyForUsSlug(slug: string, moneyline: UsBoardQuotes, lines: UsLineQuotes,
  identity: UsSlugIdentity): string | null {
  if (identity.published()) return identity.get(slug);
  return moneyline.gameKeyForSlug(slug) ?? lines.gameKeyForSlug(slug);
}
